// ─── Playground Adventure content pack ───
// Source of truth: curriculum/Playground_Adventure_Speaking_Tutor_QA.docx
// (Natalya, 2026-07-29). Playground comes BEFORE Farm in the Little Sponges
// curriculum, so the children only know Playground vocabulary: every question
// is simple naming — "What is this?", "What are these?", "Who is this?".
// No color questions, no counting questions, no favorite question.
//
// Target sentence pattern is "This is a ball." / "These are toys." /
// "This is a mom." — NOT Farm's "It is a cow." The engine's name tier already
// produces exactly these sentences (singular → "This is a X.", plural →
// "These are Xs."), so the pattern lives in the card data (count), not code.
//
// Scoring (per the approved script): the full sentence is the goal; a single
// word still earns the star, with the frog nudging toward the sentence — the
// engine's v4 sentence ladder handles this unchanged. The "also counts as
// correct" variants from the script feed extraWords (merged into the
// name-tier match vocabulary).

const QUESTIONS_PER_GAME = 10;

// Family people take "Who is this?" — the whole-family picture and every toy
// takes "What is this?" (script rule). ask overrides the engine's default
// name-tier question. NOTE: who-is-this audio must exist in
// public/audio-gemini/frog/ (generated with the other playground lines).
const WHO = { audio: '/audio/who-is-this.mp3', text: 'Who is this?' };

// Image filenames stay exactly as in Natalya's Drive library so files remain
// traceable (see adventure asset conventions).
const pool = [
  // "What is this?" — one thing
  { id: 'ball', image: '/images/playground/Ball.png', word: 'ball', plural: 'balls', count: 1, nameOnly: true },
  { id: 'doll', image: '/images/playground/Doll.png', word: 'doll', plural: 'dolls', count: 1, nameOnly: true, extraWords: ['baby doll'] },
  { id: 'car', image: '/images/playground/Car.png', word: 'car', plural: 'cars', count: 1, nameOnly: true, extraWords: ['toy car'] },
  { id: 'slide', image: '/images/playground/Slide.png', word: 'slide', plural: 'slides', count: 1, nameOnly: true },
  { id: 'jump-rope', image: '/images/playground/Jumprope.png', word: 'jump rope', plural: 'jump ropes', count: 1, nameOnly: true, extraWords: ['jumprope', 'skipping rope', 'rope'] },
  { id: 'playground', image: '/images/playground/Playground.png', word: 'playground', plural: 'playgrounds', count: 1, nameOnly: true },
  // "What are these?" — more than one
  { id: 'toys', image: '/images/playground/Toys.png', word: 'toy', plural: 'toys', count: 2, nameOnly: true },
  { id: 'blocks', image: '/images/playground/Blocks.png', word: 'block', plural: 'blocks', count: 2, nameOnly: true, extraWords: ['building blocks'] },
  // Family — people use "Who is this?"; the whole-family picture uses "What is this?"
  { id: 'mom', image: '/images/playground/Mom.png', word: 'mom', plural: 'moms', count: 1, nameOnly: true, ask: WHO, extraWords: ['mommy', 'mother', 'mama', 'mum'] },
  { id: 'dad', image: '/images/playground/Dad.png', word: 'dad', plural: 'dads', count: 1, nameOnly: true, ask: WHO, extraWords: ['daddy', 'father', 'papa'] },
  { id: 'sister', image: '/images/playground/Sister.png', word: 'sister', plural: 'sisters', count: 1, nameOnly: true, ask: WHO, extraWords: ['sis'] },
  { id: 'brother', image: '/images/playground/Brother.png', word: 'brother', plural: 'brothers', count: 1, nameOnly: true, ask: WHO, extraWords: ['bro'] },
  { id: 'family', image: '/images/playground/Family.png', word: 'family', plural: 'families', count: 1, nameOnly: true, extraWords: ['my family'] },
];

// 13-card pool; 9 name questions per session, chosen at random, + the
// favorite-toy finale as question 10 (2026-10-20, tracker #6: all 10 stars
// were already lit before the favorite question — same fix as Garden).
function buildDeck() {
  const shuffled = [...pool].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, QUESTIONS_PER_GAME - 1).map((c) => ({ ...c, tiers: ['name'] }));
}

const vocab = pool.map((c) => c.word).join(', ');

export default {
  id: 'playground',
  title: 'Playground Adventure',
  pool,
  buildDeck,
  // "What is your favorite toy?" finale added 2026-08-11 (Natalya's request).
  hasFavorite: true,
  favorite: {
    question: 'What is your favorite toy?',
    askAudio: '/audio/favorite-toy-ask.mp3',
    // Dedicated collage from Natalya for the closing screen (2026-08-12).
    image: '/images/playground/favorite-toys.png',
    judgeKind: 'toy',
    earnsStar: true,
    // No pre-baked per-toy celebration audio — every accepted toy celebrates
    // with a live named Puck line ("I love dolls too!"), synthesized once and
    // S3-cached, exactly like Farm's extras.
    allGeneric: true,
    // Toys only — the family cards (mom/dad/sister/brother) are in the card
    // pool but are NOT valid answers to "What is your favorite toy?".
    options: [
      { word: 'ball', plural: 'balls' },
      { word: 'doll', plural: 'dolls' },
      { word: 'car', plural: 'cars' },
      { word: 'slide', plural: 'slides' },
      { word: 'jump rope', plural: 'jump ropes' },
      { word: 'block', plural: 'blocks' },
      { word: 'toy', plural: 'toys' },
    ],
    // Common toys kids name beyond the card pool — matched instantly locally.
    extras: [
      { word: 'teddy bear', plural: 'teddy bears' },
      { word: 'swing', plural: 'swings' },
      { word: 'lego', plural: 'legos' },
      { word: 'truck', plural: 'trucks' },
      { word: 'train', plural: 'trains' },
      { word: 'puzzle', plural: 'puzzles' },
      { word: 'bike', plural: 'bikes' },
      { word: 'scooter', plural: 'scooters' },
      { word: 'kite', plural: 'kites' },
    ],
    synonyms: { jumprope: 'jump rope', teddy: 'teddy bear', bicycle: 'bike' },
    // Natalya's 8/12 response logic (shipped 2026-10-20, tracker #7):
    //   real toy      → "I like dolls too!"            (celebrateLine)
    //   not a toy     → "That's not a toy. Try again." (missKnown, no re-ask)
    //   unclear       → "Interesting... my favorite toy is a ball!" and the
    //                   session wraps up warmly (unclear) — no grinding.
    celebrateLine: (fav) => `I like ${fav.plural} too!`,
    missKnown: { line: "That's not a toy. Try again.", audio: '/audio/favorite-not-toy.mp3', noReask: true },
    missUnknown: { line: "That's not a toy. Try again.", audio: '/audio/favorite-not-toy.mp3', noReask: true },
    unclear: { line: 'Interesting... my favorite toy is a ball!', audio: '/audio/favorite-toy-unclear.mp3' },
    hint: { line: "Let's pick together. How about a ball or a doll?", audio: '/audio/favorite-toy-hint.mp3' },
  },
  // Background art added 2026-08-10 (playground-bg.webp, same 1672x941 as farm).
  background: '/images/playground-bg.webp',
  sttNameContext: `a toy, playground or family word (${vocab})`,
  sttFavoriteContext: 'a toy name (ball, doll, car, slide, jump rope, blocks, teddy bear, swing)',
  // Judge context (2026-08-11): sent with /api/evaluate. Without this the
  // judge prompt assumed Farm vocabulary, so native-language Playground
  // answers (Natalya's Russian test) never got the "say it in English" nudge.
  judgeTopic: 'a playground picture card — toys and playground things (ball, doll, car, slide, '
    + 'jump rope, playground, toys, blocks) and family members (mom, dad, sister, brother, family)',
  judgeHints: 'Spanish: pelota=ball, muñeca=doll, carro/coche=car, tobogán/resbaladilla=slide, '
    + 'cuerda de saltar=jump rope, bloques/cubos=blocks, juguetes=toys, parque=playground, '
    + 'mamá=mom, papá=dad, hermana=sister, hermano=brother, familia=family. '
    + 'Russian: мяч=ball, кукла=doll, машина/машинка=car, горка=slide, скакалка=jump rope, '
    + 'кубики=blocks, игрушки=toys, площадка=playground, мама=mom, папа=dad, сестра=sister, '
    + 'брат=brother, семья=family',
};
